/** First fallback poll after 1 minute while a report is still processing. */
export const REPORT_PROCESSING_FALLBACK_INTERVAL_MS = 60_000;

/** Cap: once per minute, up to 5 minutes (5 probes), then stop. */
export const REPORT_PROCESSING_FALLBACK_MAX_POLLS = 5;

/**
 * Schedule a bounded safety poll when WS `report_ready` may have been missed.
 * First run after `intervalMs`, then every `intervalMs`, at most `maxPolls` times.
 * Cadence is wall-clock (does not wait for the previous poll to finish).
 */
export function scheduleReportProcessingFallbackPoll(
  poll: () => void | Promise<void>,
  options?: {
    intervalMs?: number;
    maxPolls?: number;
  }
): () => void {
  const intervalMs = options?.intervalMs ?? REPORT_PROCESSING_FALLBACK_INTERVAL_MS;
  const maxPolls = options?.maxPolls ?? REPORT_PROCESSING_FALLBACK_MAX_POLLS;
  let pollsDone = 0;
  let cancelled = false;
  let timeoutId: number | null = null;

  const clear = () => {
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const tick = () => {
    timeoutId = null;
    if (cancelled) return;
    pollsDone += 1;
    void Promise.resolve().then(() => poll());
    if (cancelled || pollsDone >= maxPolls) return;
    timeoutId = window.setTimeout(tick, intervalMs);
  };

  timeoutId = window.setTimeout(tick, intervalMs);

  return () => {
    cancelled = true;
    clear();
  };
}

/** True while the report UI / canvas should keep waiting on generation. */
export function isReportStillProcessingStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toLowerCase();
  return s === "processing" || s === "draft" || s === "pending";
}
